import React, { useCallback, useEffect, useState } from 'react';
import CheckmarkCircleIcon from 'components/icon/checkmark-circle-icon';
import ConnectionRequestIcons from '../icons';
import { trackEvent } from 'utils/google-tag-manager';
import styles from './style.module.scss';
import { listingPageModalIds } from 'constants/test-constants';
import { useModal, useSavedListingContext, useSnackbar } from 'contexts';
import SaveHome from '../save-home';
import PhoneCTA from '../call-cta';
import { HIDE_SAVE_HOME_COOKIE } from 'constants/cookies';
import Cookies from 'js-cookie';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { LOCALE_CONNECTION_REQUEST_PATHS, Namespaces } from 'types/locales';

import type { ListingInfoType } from 'components/connection-request';

type SuccessMessageProps = {
  isInModal?: boolean;
  listing?: ListingInfoType;
  isAuthenticated?: boolean;
  readonly phoneNumber?: string;
};

export default function SuccessMessage({ isInModal, listing, isAuthenticated, phoneNumber }: SuccessMessageProps) {
  const { t } = useTranslation(Namespaces.CONNECTION_REQUEST);
  const { isSavedListing } = useSavedListingContext();
  const { setSnackbarData } = useSnackbar();
  const { closeModal } = useModal();
  const [hasRespondedToSaveHome, setHasRespondedToSaveHome] = useState(false);
  const isFavourited = !!listing && isSavedListing(null, listing.id);
  const showSaveHome = listing && isAuthenticated && !isFavourited && !hasRespondedToSaveHome && !Cookies.get(HIDE_SAVE_HOME_COOKIE);

  const handleSaveHomeResponse = useCallback(() => {
    if (hasRespondedToSaveHome && isFavourited) {
      setSnackbarData(prev => ({
        ...prev,
        visible: true,
        message: <p>{t(LOCALE_CONNECTION_REQUEST_PATHS.SUCCESS_MESSAGE_SAVE_HOME_LINK_MESSAGE, { defaultValue: "You've added this property to" })} <br/><Link href="/account/saved/favourites" onClick={closeModal}>{t(LOCALE_CONNECTION_REQUEST_PATHS.SUCCESS_MESSAGE_SAVE_HOME_LINK, { defaultValue: 'My Favourites' })}</Link></p>,
      }));
      if (isInModal) {
        closeModal();
      }
    }
  }, [hasRespondedToSaveHome, isFavourited, closeModal, isInModal, setSnackbarData, t]);

  useEffect(() => {
    handleSaveHomeResponse();
  }, [handleSaveHomeResponse]);

  if (isInModal) { // mobile view
    return (
      <div className={styles['modal-component']}>
        <ConnectionRequestIcons />
        <h1>{t(LOCALE_CONNECTION_REQUEST_PATHS.SUCCESS_MESSAGE_THANK_YOU, { defaultValue: 'Thank you!' })}</h1>
        <p>{t(LOCALE_CONNECTION_REQUEST_PATHS.SUCCESS_MESSAGE_WILL_BE_IN_TOUCH, { defaultValue: 'We\'ll be in touch soon. If you\'re in a hurry, don\'t hesitate to reach out' })}
          <a href={`tel:${phoneNumber}`} onClick={() => trackEvent('click_call')}>
            {phoneNumber}
          </a>
          .
        </p>
        <PhoneCTA phoneNumber={phoneNumber} />
        {showSaveHome && <SaveHome listing={listing} setHasRespondedToFavourites={setHasRespondedToSaveHome} />}
      </div>
    );
  }

  return (
    <>
      <div className={styles.component} data-testid={listingPageModalIds.successMessage}>
        <div className={styles.success}>
          <CheckmarkCircleIcon className={styles.checkmark} />
          <div className={styles.right}>
            <h1>{t(LOCALE_CONNECTION_REQUEST_PATHS.SUCCESS_MESSAGE_THANKS, { defaultValue: 'Thanks!' })}</h1>
            <p>{t(LOCALE_CONNECTION_REQUEST_PATHS.SUCCESS_MESSAGE_WILL_FOLLOW_UP, { defaultValue: 'We will follow up with you.' })}</p>
            <PhoneCTA phoneNumber={phoneNumber} />
          </div>
        </div>
        {showSaveHome && <SaveHome listing={listing} setHasRespondedToFavourites={setHasRespondedToSaveHome} />}
      </div>
    </>
  );
}
